import { useEffect, useState } from "react";
import { UserPlus, Trash2, KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errText, shortDate } from "./api";

const AddMember = ({ onAdded }) => {
  const blank = { name: "", email: "", password: "", role: "member" };
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/admin/team", f); toast.success(`${f.name} can now sign in`); setF(blank); onAdded(); }
    catch (err) { toast.error(errText(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="rounded-2xl bg-white p-5" data-testid="team-add-form">
      <h2 className="font-display text-2xl font-black uppercase">Add a team member</h2>
      <div className="mt-3 grid gap-3 md:grid-cols-2">
        <input required minLength={2} className="inp" placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="team-name-input" />
        <input required type="email" className="inp" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="team-email-input" />
        <input required minLength={8} className="inp" placeholder="Starting password (8+ characters)" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="team-password-input" />
        <select className="inp" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} data-testid="team-role-select">
          <option value="member">Team member: leads, numbers, SEO</option>
          <option value="admin">Owner: can also manage the team</option>
        </select>
      </div>
      <button disabled={busy} className="mt-3 flex items-center gap-2 rounded-full bg-slate-900 px-5 py-2.5 text-sm font-bold text-white disabled:opacity-60" data-testid="team-add-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}Add</button>
    </form>
  );
};

const MyPassword = () => {
  const [f, setF] = useState({ current_password: "", new_password: "" });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/change-password", f); toast.success("Password changed"); setF({ current_password: "", new_password: "" }); }
    catch (err) { toast.error(errText(err)); }
  };
  return (
    <form onSubmit={submit} className="rounded-2xl bg-white p-5" data-testid="my-password-form">
      <h2 className="font-display text-2xl font-black uppercase">Change my password</h2>
      <div className="mt-3 grid gap-3 md:grid-cols-2">
        <input required type="password" autoComplete="current-password" className="inp" placeholder="Current password" value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} data-testid="my-current-password" />
        <input required minLength={8} type="password" autoComplete="new-password" className="inp" placeholder="New password (8+ characters)" value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} data-testid="my-new-password" />
      </div>
      <button className="mt-3 rounded-full bg-slate-900 px-5 py-2.5 text-sm font-bold text-white" data-testid="my-password-save">Save</button>
    </form>
  );
};

export default function Team({ me }) {
  const [team, setTeam] = useState([]);
  const isOwner = me.role === "admin";
  const load = () => api.get("/admin/team").then((r) => setTeam(Array.isArray(r.data) ? r.data : [])).catch(() => setTeam([]));
  useEffect(() => { load(); }, []);
  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.name}? They won't be able to sign in.`)) return;
    try { await api.delete(`/admin/team/${u.id}`); toast.success("Removed"); load(); } catch (e) { toast.error(errText(e)); }
  };
  const reset = async (u) => {
    const pw = window.prompt(`New password for ${u.name} (8+ characters)`);
    if (!pw) return;
    try { await api.post(`/admin/team/${u.id}/password`, { password: pw }); toast.success("Password reset. Share it with them in person."); } catch (e) { toast.error(errText(e)); }
  };
  return (
    <div className="grid gap-4" data-testid="admin-team">
      <h1 className="font-display text-4xl font-black uppercase">Team</h1>
      <div className="rounded-2xl bg-white p-5">
        <ul className="divide-y divide-slate-100">
          {team.map((u) => (
            <li key={u.id} className="flex flex-wrap items-center justify-between gap-3 py-3" data-testid={`team-member-${u.email}`}>
              <div><p className="font-bold">{u.name} <span className="ml-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold">{u.role === "admin" ? "Owner" : "Team"}</span></p>
                <p className="text-sm text-slate-500">{u.email} · added {shortDate(u.created_at)}</p></div>
              {isOwner && u.id !== me.id && (
                <div className="flex gap-2">
                  <button onClick={() => reset(u)} className="flex items-center gap-1 rounded-full border border-slate-300 px-3 py-2 text-xs font-semibold" data-testid="team-reset-button"><KeyRound className="h-3.5 w-3.5" />Reset password</button>
                  <button onClick={() => remove(u)} className="flex items-center gap-1 rounded-full border border-red-200 px-3 py-2 text-xs font-semibold text-red-700" data-testid="team-remove-button"><Trash2 className="h-3.5 w-3.5" />Remove</button>
                </div>
              )}
            </li>
          ))}
        </ul>
      </div>
      {isOwner && <AddMember onAdded={load} />}
      <MyPassword />
    </div>
  );
}
